import Vue from 'vue';
import axios from 'axios';
import { tokenService } from '../services';
import { EXPORTADORES } from '../helpers/exportadores';

// Cada cuánto se pregunta al backend si la planilla ya está lista.
const INTERVALO_CONSULTA_MS = 2000;
// Feature flag del backend: encendida, las planillas las genera el worker.
const FLAG_WORKER = 'worker_segundo_plano';

const API = process.env.VUE_APP_API_URL;

function workerActivo() {
    // El backend responde 404 si la flag no existe: cuenta como apagada.
    return axios.get(`${API}/feature_flags/${FLAG_WORKER}/`, tokenService.getAuthentication())
        .then((response) => response.data === true)
        .catch(() => false);
}

function descargarArchivo(url, params) {
    return axios({
        url: url,
        method: 'GET',
        responseType: 'blob',
        params: params,
        ...tokenService.getAuthentication(),
    }).then((response) => {
        const contentDisposition = response.headers['content-disposition'];
        let fileName = 'downloaded-file.xlsx'; // Valor por defecto

        if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
            if (fileNameMatch && fileNameMatch.length === 2) {
                fileName = fileNameMatch[1];
            }
        }

        const blob = new Blob([response.data], { type: response.data.type });
        const fileURL = window.URL.createObjectURL(blob);
        const fileLink = document.createElement('a');

        fileLink.href = fileURL;
        fileLink.setAttribute('download', fileName);
        document.body.appendChild(fileLink);
        fileLink.click();
        document.body.removeChild(fileLink);
        window.URL.revokeObjectURL(fileURL);
    });
}

// Sigue las exportaciones de planillas a nivel de aplicación, para que se
// descarguen al terminar aunque el usuario se vaya de la vista de exportadores.
export const exportaciones = {
    namespaced: true,
    state: {
        // url del exportador -> { title, id }; id es null si no pasa por el worker.
        enCurso: {},
    },
    getters: {
        generando: (state) => (url) => Boolean(state.enCurso[url]),
    },
    actions: {
        // Con la flag del worker encendida, el backend encola la planilla y se
        // consulta su estado hasta que esté lista; apagada (o sin crear), el
        // backend la genera en el mismo request y se descarga directo.
        exportar({ commit, dispatch }, { exportador, fechas }) {
            commit('iniciar', { url: exportador.url, title: exportador.title, id: null });
            dispatch('alert/success', `Generando "${exportador.title}", se descargará al terminar`, { root: true });
            const url = `${API}/exportadores/${exportador.url}/`;

            return workerActivo().then((activo) => {
                if (!activo) {
                    return descargarArchivo(url, fechas)
                        .then(() => commit('terminar', exportador.url))
                        .catch(() => dispatch('fallar', exportador.url));
                }
                return axios.post(url, fechas, tokenService.getAuthentication())
                    .then((response) => {
                        commit('iniciar', { url: exportador.url, title: exportador.title, id: response.data.id });
                        dispatch('esperar', exportador.url);
                    })
                    .catch(() => dispatch('fallar', exportador.url));
            });
        },
        // Al iniciar sesión pregunta una vez al backend si quedaron
        // exportaciones sin terminar y retoma su seguimiento.
        reanudar({ commit, dispatch }) {
            return axios.get(`${API}/workers_en_curso/`, tokenService.getAuthentication())
                .then((response) => {
                    response.data
                        .filter((trabajo) => trabajo.tipo === 'generar_exportacion')
                        .forEach((trabajo) => {
                            const url = trabajo.argumentos.exportador;
                            const exportador = EXPORTADORES.find((e) => e.url === url);
                            commit('iniciar', { url, title: exportador ? exportador.title : url, id: trabajo.id });
                            dispatch('esperar', url);
                        });
                })
                .catch(() => {});
        },
        esperar({ state, commit, dispatch }, url) {
            const exportacion = state.enCurso[url];
            if (!exportacion || !exportacion.id) return;

            axios.get(`${API}/exportaciones/${exportacion.id}/`, tokenService.getAuthentication())
                .then((response) => {
                    if (response.data.estado === 'completado') {
                        descargarArchivo(`${API}/exportaciones/${exportacion.id}/archivo/`)
                            .then(() => {
                                commit('terminar', url);
                                dispatch('alert/success', `"${exportacion.title}" descargado`, { root: true });
                            })
                            .catch(() => dispatch('fallar', url));
                    } else if (response.data.estado === 'fallido') {
                        dispatch('fallar', url);
                    } else {
                        setTimeout(() => dispatch('esperar', url), INTERVALO_CONSULTA_MS);
                    }
                })
                .catch((error) => {
                    // 404: el worker ya borró la exportación; no hay nada que avisar.
                    if (error.response && error.response.status === 404) {
                        commit('terminar', url);
                    } else {
                        dispatch('fallar', url);
                    }
                });
        },
        fallar({ state, commit, dispatch }, url) {
            const exportacion = state.enCurso[url];
            commit('terminar', url);
            if (exportacion) {
                dispatch('alert/error', `No se pudo generar "${exportacion.title}"`, { root: true });
            }
        },
        olvidar({ commit }) {
            commit('olvidar');
        },
    },
    mutations: {
        iniciar(state, { url, title, id }) {
            Vue.set(state.enCurso, url, { title, id });
        },
        terminar(state, url) {
            Vue.delete(state.enCurso, url);
        },
        olvidar(state) {
            state.enCurso = {};
        },
    },
};
